export const h2r = (hex: string) => {
  const short = hex.length === 4;
  const result = short
    ? /^#?([a-f\d]{1})([a-f\d]{1})([a-f\d]{1})$/i.exec(hex)
    : /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);

  return result ? [
    parseInt(`${result[1]}${short ? result[1] : ''}`, 16),
    parseInt(`${result[2]}${short ? result[2] : ''}`, 16),
    parseInt(`${result[3]}${short ? result[3] : ''}`, 16),
  ] : null;
};

// eslint-disable-next-line no-bitwise
export const r2h = (rgb: [number, number, number]) => `#${((1 << 24) + (rgb[0] << 16) + (rgb[1] << 8) + rgb[2])
  .toString(16)
  .slice(1)}`;

export const interpolateColor = (color1, color2, factor = 0.5) => {
  const result = color1.slice();
  // eslint-disable-next-line no-plusplus
  for (let i = 0; i < 3; i++) {
    result[i] = Math.round(result[i] + factor * (color2[i] - color1[i]));
  }
  return result;
};

export enum ColorsPaletteChoices {
  PURPLE='purple',
  BLUE='blue',
  RED_BLUE='red_blue',
  GREEN_MAGENTA='green_magenta',
  BLU_RED='blu_red',
  MAGENTA_GREEN='magenta_green'
}

export const colorsPalette = (typeof window !== 'undefined' && typeof localStorage !== 'undefined'
  && (localStorage.getItem('testUserColorsPalette') || localStorage.getItem('userColorsPalette')))
  || 'blu_red';

export const colorsPaletteOptions = [
  { id: '0', value: ColorsPaletteChoices.BLUE, label: 'blue' },
  { id: '1', value: ColorsPaletteChoices.PURPLE, label: 'purple' },
  { id: '2', value: ColorsPaletteChoices.RED_BLUE, label: 'red blue' },
  { id: '3', value: ColorsPaletteChoices.BLU_RED, label: 'blu red' },
  { id: '4', value: ColorsPaletteChoices.GREEN_MAGENTA, label: 'green magenta' },
  { id: '5', value: ColorsPaletteChoices.MAGENTA_GREEN, label: 'magenta green' },
];

export const RED_BLUE_2_COLORS = ['#3268EF', '#FB6161'];

export const BLU_RED_2_COLORS = ['#FB6161', '#3268EF'];

export const get2ColorsPalette = (colorsPalette: string) => {
  switch (colorsPalette) {
    case 'red_blue':
      return RED_BLUE_2_COLORS;
    case 'blu_red':
      return BLU_RED_2_COLORS;
    case 'purple':
      return getPrimaryColorGradient(3).slice(1);
    default:
      return getPrimaryColorGradient(2);
  }
};

export const getPrimaryColorGradient = (num: number, palette : string = colorsPalette) => {
  // Green Variant (original)
  const greenColors = [
    '#CAFFF5',
    '#43FCDB',
    '#00DDB5',
    '#00B292',
    '#00876F',
    '#004D41',
  ];

  // Purple Variant
  const purpleColors = [
    '#ECE7FC',
    '#C7B6F5',
    '#8F6CEC',
    '#450ADF',
    '#290686',
    '#150343',
  ];

  // Green/Red Variant
  const greenMagentaColors = [
    '#17FFC2',
    '#4DD7BB',
    '#73BCA9',
    '#C49DA8',
    '#D85785',
    '#E6296E',
  ];

  // Red Blue Variant
  const redBlue = [
    '#9BD8F9',
    '#61A2FB',
    '#3268EF',
    '#F99B9B',
    '#FB6161',
    '#D81D1D',
  ];

  // Blue Variant
  const blueColors = [
    '#9BD8F9',
    '#61A2FB',
    '#3268EF',
    '#1D4DD8',
    '#1E418D',
    '#283951',
  ];

  const getColorsPalette = (option: string) => {
    if (option === 'green') {
      return [
        ...([3, 4, 5, 6].includes(num) && [greenColors[0]] || []),
        ...([5, 6].includes(num) && [greenColors[1]] || []),
        ...[greenColors[2]],
        ...([4, 5, 6].includes(num) && [greenColors[3]] || []),
        ...([2, 3, 5, 6].includes(num) && [greenColors[4]] || []),
        ...([4, 6].includes(num) && [greenColors[5]] || []),
      ];
    }
    if (option === 'purple') {
      return [
        ...([4, 5, 6].includes(num) && [purpleColors[0]] || []),
        ...([2, 3, 5, 6].includes(num) && [purpleColors[1]] || []),
        ...([3, 4, 5, 6].includes(num) && [purpleColors[2]] || []),
        ...[purpleColors[3]],
        ...([4, 5, 6].includes(num) && [purpleColors[4]] || []),
        ...([6].includes(num) && [purpleColors[5]] || []),
      ];
    }
    if (option === 'green_magenta') {
      return [
        ...([3, 4, 5, 6].includes(num) && [greenMagentaColors[0]] || []),
        ...([2, 5, 6].includes(num) && [greenMagentaColors[1]] || []),
        ...([3, 4, 6].includes(num) && [greenMagentaColors[2]] || []),
        ...([5, 6].includes(num) && [greenMagentaColors[3]] || []),
        ...([4, 5, 6].includes(num) && [greenMagentaColors[4]] || []),
        ...[greenMagentaColors[5]],
      ];
    }
    if (option === 'magenta_green') {
      // inverted green_magenta: same colors, reversed order
      return [
        ...[greenMagentaColors[5]],
        ...([4, 5, 6].includes(num) && [greenMagentaColors[4]] || []),
        ...([5, 6].includes(num) && [greenMagentaColors[3]] || []),
        ...([3, 4, 6].includes(num) && [greenMagentaColors[2]] || []),
        ...([2, 5, 6].includes(num) && [greenMagentaColors[1]] || []),
        ...([3, 4, 5, 6].includes(num) && [greenMagentaColors[0]] || []),
      ];
    }
    if (option === 'red_blue') {
      return [
        ...([5, 6].includes(num) && [redBlue[0]] || []),
        ...([3, 4, 5, 6].includes(num) && [redBlue[1]] || []),
        ...([1, 2, 3, 4, 5, 6].includes(num) && [redBlue[2]] || []),
        ...([4, 5, 6].includes(num) && [redBlue[3]] || []),
        ...([2, 3, 4, 5, 6].includes(num) && [redBlue[4]] || []),
        ...([6].includes(num) && [redBlue[5]] || []),
      ];
    }
    if (option === 'blu_red') {
      // inverted red_blue: same colors, reversed order
      return [
        ...([6].includes(num) && [redBlue[5]] || []),
        ...([2, 3, 4, 5, 6].includes(num) && [redBlue[4]] || []),
        ...([4, 5, 6].includes(num) && [redBlue[3]] || []),
        ...([1, 2, 3, 4, 5, 6].includes(num) && [redBlue[2]] || []),
        ...([3, 4, 5, 6].includes(num) && [redBlue[1]] || []),
        ...([5, 6].includes(num) && [redBlue[0]] || []),
      ];
    }
    if (option === 'blue') {
      return [
        ...([4, 5, 6].includes(num) && [blueColors[0]] || []),
        ...([2, 3, 5, 6].includes(num) && [blueColors[1]] || []),
        ...([3, 4, 5, 6].includes(num) && [blueColors[2]] || []),
        ...[blueColors[3]],
        ...([4, 5, 6].includes(num) && [blueColors[4]] || []),
        ...([6].includes(num) && [blueColors[5]] || []),
      ];
    }
    return [
      ...([5, 6].includes(num) && [redBlue[0]] || []),
      ...([3, 4, 5, 6].includes(num) && [redBlue[1]] || []),
      ...([1, 2, 3, 4, 5, 6].includes(num) && [redBlue[2]] || []),
      ...([4, 5, 6].includes(num) && [redBlue[3]] || []),
      ...([2, 3, 4, 5, 6].includes(num) && [redBlue[4]] || []),
      ...([6].includes(num) && [redBlue[5]] || []),
    ]; // fallback to red blue as default
  };

  return getColorsPalette(palette);
};

export const getColorsCombination = () => {
  const variableColors = getPrimaryColorGradient(6, colorsPalette);
  const order = [1, 3, 0, 4, 2, 5];
  const colors: string[] = [];
  order.forEach((n) => {
    colors.push(variableColors[n]);
  });
  return colors;
};

export const getFrontColorFromBG = (hex, darkColor = '#828282', lightColor = '#ffffff') => {
  const hexcolor = hex.replace('#', '');
  const r = parseInt(hexcolor.substr(0, 2), 16);
  const g = parseInt(hexcolor.substr(2, 2), 16);
  const b = parseInt(hexcolor.substr(4, 2), 16);
  const yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000;
  return (yiq >= 128) ? darkColor : lightColor;
};
